import * as config from '../config';
import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import DomainModel from '../DomainModel';
import ModelLoader from '../ModelLoader';
// @ts-ignore
import landmarks from '../data/jarlin_cabinets_landmarks.json';
import logging from '../logging';
import traverse from '../utils/traverseGenerator';
import { BufferGeometry, Group, Matrix4, Mesh,  Vector2, Vector3, Vector4 } from 'three';
import { Cabinet, Room, Wall } from '../domain';
import { Vector3 as DomainVector3 } from '../math/vector3';
import { EdgeList, SweptMesh, ThreeJSMesh } from '../mw/prims';
import { cabinets, cabinetDepth, cabinetWidth, lineMaterial, cabinetHeight } from '../data/JarlinCatalogData';
import { lookahead, pairwise } from '../utils';
import { uvDimensions, uvScale } from '../IIID';

const log = logging('view');
const ml = ModelLoader.getInstance();

const needsCrownMolding = (cabinet: Cabinet) => {
    return cabinets.boolean(cabinet.sku, 'crown');
}

const crownYPosition = (cabinet: Cabinet) => {
    switch(cabinets.string(cabinet.sku, 'model')){
        case 'WP':
        case 'OC':
            return cabinetHeight(cabinet.sku);
        default:
            return cabinet.position.y;
    }
}

const isFiller = (cabinet: Cabinet) => {
    return cabinet.sku.startsWith('BF') || cabinet.sku.startsWith('WF');
}

const firstCrownedCabinet = (wall: Wall) => {
    for(const cabinet of wall.cabinets) {
        if(needsCrownMolding(cabinet)) {
            return cabinet;
        }
    }
}

const vapprox = (a: Vector3, b: Vector3) => {
    return (
        Math.abs(a.x - b.x) +
        Math.abs(a.y - b.y) +
        Math.abs(a.z - b.z)
    ) < 0.001;
}

const gatherCabinetClusters = (room: Room) => {

    let
        clusters: Cabinet[][] = [],
        cluster: Cabinet[] = [];

    for(const [wall, nextWall] of lookahead(room.walls))
    {
        const cabinets = wall.cabinets.filter(needsCrownMolding);

        for(const [cabinet, nextCabinet] of lookahead(cabinets))
        {
            cluster.push(cabinet);

            const
                width = cabinetWidth(cabinet.sku),
                depth = isFiller(cabinet)
                    ? cabinet.position.z
                    : cabinetDepth(cabinet.sku),
                rightEdge = cabinet.position.x + width,
                adjacent =
                    nextCabinet != null
                    && rightEdge == nextCabinet.position.x
                    && crownYPosition(cabinet) == crownYPosition(nextCabinet);

            if(adjacent) {
                continue;
            }

            // If we're in a corner, and the first cabinet on the next wall is
            // inside our
            if(nextCabinet == null && nextWall && nextWall.cabinets.length > 0) {
                const first: Cabinet = firstCrownedCabinet(nextWall);
                if(first
                   && first.position.x <= depth
                   && crownYPosition(cabinet) == crownYPosition(first))
                {
                    continue;
                }
            }

            // If we haven't continued the cluster above, then we split it
            // by default

            clusters.push(cluster);
            cluster = [];
        }
    }
    return clusters;
}

type XYZ = {x: number, y: number, z: number};

const copy = (src: XYZ, dest: XYZ) => {
    dest.x = src.x;
    dest.y = src.y;
    dest.z = src.z;
}

const isDomainModel3D = (model: DomainModel): model is DomainModel3D => {
    return 'position' in model && model["position"] instanceof DomainVector3
        && 'rotation' in model && model["rotation"] instanceof DomainVector3
}

// another cut-down transform class for the very specific purpose of getting
// cabinet positions in world space for adding crown molding globally
export class Transform {
    scratch: Matrix4 = new Matrix4();
    self: Matrix4 = new Matrix4();
    vec4: Vector4 = new Vector4();

    init(model: DomainModel3D) {
        this.self.identity();
        const parents: DomainModel3D[] = [model];
        let parent = model.parent;
        while(parent != null && isDomainModel3D(parent)) {
            parents.push(parent);
            parent = parent.parent;
        }

        parents.reverse();

        for(const parent of parents) {
            this.translate(parent.position);
            this.rotate(parent.rotation);
        }
    }

    toWorldPosition(src: XYZ, dest: XYZ) {
        copy(src, this.vec4);
        this.vec4.w = 1;
        this.vec4.applyMatrix4(this.self);
        copy(this.vec4, dest);
    }

    translate({x, y, z}: XYZ) {
        this.scratch.makeTranslation(x, y, z);
        this.self.multiply(this.scratch);
    }

    /**
     * Rotates X, Y, and Z in that order. Our DomainModel3D class is always in
     * XYZ order.
     */
    rotate({x, y, z}: XYZ) {
        this.scratch.makeRotationX(x);
        this.self.multiply(this.scratch);
        this.scratch.makeRotationY(y);
        this.self.multiply(this.scratch);
        this.scratch.makeRotationZ(z);
        this.self.multiply(this.scratch);
    }
}

const renderCabinetClusters = async (room: Room, clusters: Cabinet[][]) => {
    const
        group = new Group(),
        meshes: Mesh[] = [],
        v = new Vector3(),
        t = new Transform();

    for(const cluster of clusters) {
        if(cluster.length == 0) continue;

        const
            first = cluster[0],
            last = cluster[cluster.length - 1],
            plan = new EdgeList(),
            verts: Vector3[] = [];

        t.init(first.parent as Wall);

        t.toWorldPosition({x: first.position.x, y:0, z:0}, v);
        verts.push(v.clone());

        for(const cabinet of cluster) {
            // the cluster may span walls, so we re-initialize for every
            // cabinet.
            t.init(cabinet.parent as Wall);
            if(cabinet.sku in landmarks) {
                const pos = landmarks[cabinet.sku].crown
                for(let i=0; i < pos.length; i+=2) {
                    t.toWorldPosition({
                        x: cabinet.position.x + pos[i],
                        y: 0,
                        z: cabinet.position.z + pos[i+1]},
                        v);
                    verts.push(v.clone());
                }
            } else {
                const depth = cabinet.position.z + (isFiller(cabinet)
                    ? 0
                    : cabinetDepth(cabinet.sku));
                // TODO: clamp X so it's no closer than 12" from the START of the
                // wall, unless this is the first cabinet
                t.toWorldPosition({
                    x: cabinet.position.x,
                    y: 0,
                    z: depth},
                    v);
                verts.push(v.clone());

                // TODO: clamp X so it's no closer than 12" from the END of the
                // wall, unless this is the last cabinet
                t.toWorldPosition({
                    x: cabinet.position.x + cabinetWidth(cabinet.sku),
                    y: 0,
                    z: depth},
                    v);
                verts.push(v.clone());
            }
        }

        t.init(last.parent as Wall);
        if(last.sku in landmarks) {
            const pos = landmarks[last.sku].last;
            t.toWorldPosition({
                x: last.position.x + pos[0],
                y:0,
                z: last.position.z + pos[1]},
                v);
        } else {
            t.toWorldPosition(
                {x: last.position.x + cabinetWidth(last.sku), y:0, z:0}, v);
        }
        verts.push(v.clone());

        // Filter out duplicates because they mess up the EdgeList plan
        const filtered: Vector3[] = [];
        for(const [v, next] of lookahead(verts)) {
            if(next && vapprox(v, next)) {
                continue;
            }
            filtered.push(v);
        }

        filtered.reverse();
        for(const [a, b] of pairwise(filtered)) {
            plan.push(a.x,a.z, 0,0, b.x,b.z, 0,0);
        }

        const profileObj = await ml.get(
            `${config.geometry}/crown_jarlin_${room.molding.crown}.glb`);
        let profileGeo: BufferGeometry;
        for(const obj of traverse(profileObj)) {
            if(obj instanceof Mesh) {
                profileGeo = obj.geometry;
            }
        }
        const profile = new EdgeList();
        try {
            profile.fromBufferGeometry(profileGeo);
        } catch(error) {
            log.error(error);
            return;
        }

        const sw = new SweptMesh(profile, plan);
        const geo = ThreeJSMesh.toBufferGeometry(sw.generate());
        const mat = lineMaterial(room.line);
        const mesh = new Mesh(geo, mat);
        mesh.castShadow = mesh.receiveShadow = true;
        mesh.position.y = crownYPosition(last);
        group.add(mesh);

        meshes.push(mesh);
    }

    if(meshes.length > 0) {
        const ref = meshes[0];
        const uvDims = uvDimensions(ref.geometry);
        const scale = 1 / Math.max(uvDims.width, uvDims.height);
        const scaleVec = new Vector2(scale, scale);
        for(const mesh of meshes) {
            uvScale(mesh.geometry, scaleVec);
        }
    }

    return group;
}


export default class CrownMoldingView extends BaseView {
    declare model: Room;

    constructor(model: Room, app: Application) {
        super(model, app);

        this.updateAppearance = this.updateAppearance.bind(this);

        this.init();
    }

    protected make(): void {
        this.name = 'Crown Molding';
        this.userData.isParametric = true;
    }

    protected bind(): void {
        this.model.bind('walls', this.updateAppearance);
    }

    updateAppearance() {
        this.updates.queue(
            async () => {
                if(this.model.molding.crown) {
                    const
                        clusters = gatherCabinetClusters(this.model),
                        group = await renderCabinetClusters(this.model, clusters);
                    return group;
                }
            },
            (group: Group | undefined) => {
                if(group) {
                    for(const child of Array.from(this.children)) {
                        child.removeFromParent();
                    }
                    this.add(group);
                }
            })
    }
}
